import apiClient from '../api/apiClient';
import { MemberRegistrationResponse } from '../../types/memberRegistration';
import { ContactDetails, PhoneContact, EmailContact } from '../../types/mfa';
import { maskPhoneNumber, maskEmail, isValidPhone, isValidEmail } from '../../utils/contactMasking';

/**
 * Contact Service
 * Handles SOA API calls for member registration details and contact information
 */
class ContactService {
  /**
   * Fetches member registration details from SOA API
   * 
   * @param userName - The username to fetch details for
   * @param sessionId - The session ID for authentication
   * @returns ContactDetails object with phones and emails, or null if no contacts
   */
  static async getMemberRegistrationDetails(
    userName: string,
    sessionId: string
  ): Promise<ContactDetails | null> {
    try {
      const response = await apiClient.post<MemberRegistrationResponse>(
        `/carelonrx/memberRegistrationDetails?sessionId=${sessionId}`,
        {}
      );

      return this.transformToContactDetails(response.data);
    } catch (error) {
      console.error('Failed to fetch member registration details:', error);
      throw error;
    }
  }

  /**
   * Transforms SOA API response to ContactDetails format with priority logic
   * 
   * Email Priority:
   *   1. Member Email (response.email.email) - shown first
   *   2. Carelon Pharmacy Email (carelonPharmacyContact.emailAddress) - shown second
   * 
   * Phone Priority:
   *   1. All Member Phones (response.phone[]) - shown first
   *   2. Carelon Pharmacy Phone (carelonPharmacyContact.phoneNumber) - shown last
   * 
   * Deduplication: Remove duplicate values while preserving priority order
   * 
   * @param response - The SOA API response
   * @returns ContactDetails object or null if no contacts available
   */
  static transformToContactDetails(
    response: MemberRegistrationResponse
  ): ContactDetails | null {
    const emailOptions: EmailContact[] = [];
    const phoneOptions: PhoneContact[] = [];

    // ===== EMAIL PRIORITY =====
    
    // Priority 1: Member Email
    if (response.email?.email && response.email.email.trim() && isValidEmail(response.email.email)) {
      emailOptions.push({
        id: response.email.emailUid || crypto.randomUUID(),
        maskedEmail: maskEmail(response.email.email),
        fullEmail: response.email.email
      });
    }
    
    // Priority 2: Carelon Pharmacy Email
    if (response.carelonPharmacyContact?.emailAddress && 
        response.carelonPharmacyContact.emailAddress.trim() && 
        isValidEmail(response.carelonPharmacyContact.emailAddress)) {
      emailOptions.push({
        id: crypto.randomUUID(),
        maskedEmail: maskEmail(response.carelonPharmacyContact.emailAddress),
        fullEmail: response.carelonPharmacyContact.emailAddress
      });
    }
    
    // Deduplicate emails while preserving priority order
    const uniqueEmails = this.deduplicateContacts(emailOptions, 'fullEmail');

    // ===== PHONE PRIORITY =====
    
    // Priority 1: All Member Phones
    if (response.phone && Array.isArray(response.phone)) {
      response.phone.forEach(phoneInfo => {
        if (phoneInfo.phone && phoneInfo.phone.trim() && isValidPhone(phoneInfo.phone)) {
          phoneOptions.push({
            id: phoneInfo.phoneUid || crypto.randomUUID(),
            maskedNumber: maskPhoneNumber(phoneInfo.phone),
            fullNumber: phoneInfo.phone
          });
        }
      });
    }
    
    // Priority 2: Carelon Pharmacy Phone
    if (response.carelonPharmacyContact?.phoneNumber && 
        response.carelonPharmacyContact.phoneNumber.trim() && 
        isValidPhone(response.carelonPharmacyContact.phoneNumber)) {
      phoneOptions.push({
        id: crypto.randomUUID(),
        maskedNumber: maskPhoneNumber(response.carelonPharmacyContact.phoneNumber),
        fullNumber: response.carelonPharmacyContact.phoneNumber
      });
    }
    
    // Deduplicate phones while preserving priority order
    const uniquePhones = this.deduplicateContacts(phoneOptions, 'fullNumber');

    // ===== NO CONTACT SCENARIO =====
    
    // Return null if no contacts at all (triggers "No Contact" page)
    if (uniquePhones.length === 0 && uniqueEmails.length === 0) {
      return null;
    }

    return { 
      phones: uniquePhones, 
      emails: uniqueEmails 
    };
  }

  /**
   * Deduplicates contacts while preserving order
   * For phone numbers: normalizes to digits-only before comparison
   * For emails: comparison is case-insensitive and trims whitespace
   * 
   * @param contacts - Array of contacts to deduplicate
   * @param uniqueKey - Key to use for uniqueness check
   * @returns Deduplicated array
   */
  private static deduplicateContacts<T extends Record<string, any>>(
    contacts: T[],
    uniqueKey: keyof T
  ): T[] {
    const seen = new Set<string>();
    return contacts.filter(contact => {
      let value = String(contact[uniqueKey] || '').toLowerCase().trim();
      
      if (uniqueKey === 'fullNumber') {
        value = value.replace(/\D/g, ''); 
      }
      
      if (!value || seen.has(value)) {
        return false;
      }
      seen.add(value);
      return true;
    });
  }
}

export default ContactService;
